// MaterialSearchPage 配下で共有する UI 非依存の純関数群(および共有 UI 断片)。
// 材料の到達段階(最も進んだ非削除ステージ)。web summary と同じ IsAnyDeleted 判定。
// ラベル/色は mock(traceability-mockup)準拠。末端は「消費済（施工完了）」default。

import { ReactNode } from "react";
import { DataLink } from "../../../components/links/EntityLinks";
import { IsAnyDeleted } from "../../../util/DataExtensions";
import { searchedMaterial } from "../../../schema/console/material";
import { getQrConsumedRatio, getQrRemainingRatio, isQrFullyConsumed, isQrPartiallyConsumed } from "../../../util/consumption";

export type StageKey = "issued" | "shipped" | "in_stock" | "planned" | "partial" | "consumed";
type MaterialStage = { key: StageKey; label: string; color: "default" | "primary" | "info" | "warning" | "success" };

// searchedMaterial.Consumptions(このQR分のみ)を util/consumption.ts の関数へ渡す形に変換する。
function toConsumptionLike(m: searchedMaterial) {
  return m.Consumptions.map((c) => ({ material_qr_id: m.id, used_ratio: c.used_ratio, deleted_at: c.deleted_at }));
}

// このQRの累計消費割合(0..1)。Consumptions が空(消費明細未対応レスポンス／旧データ)のときは 0 を返す。
export function consumedRatioOfQr(m: searchedMaterial): number {
  return getQrConsumedRatio(toConsumptionLike(m), m.id);
}

// このQRの残割合(0..1)。
export function remainingRatioOfQr(m: searchedMaterial): number {
  return getQrRemainingRatio(toConsumptionLike(m), m.id);
}

// 消費済判定: Construction/Plan/Work が非削除で、かつ「有効な ConstructionRecord が1件以上」
// または「Construction.finished_at が 非null(完了保存)」なら消費済とみなす。
// 新モデルの完了保存では ConstructionRecord を作らないケースがあるため、finished_at も見る。
export function isConsumedMaterial(m: searchedMaterial): boolean {
  if (IsAnyDeleted(m.Construction, m.Construction?.ConstructionPlan, m.Construction?.ConstructionPlan?.Work)) {
    return false;
  }
  const hasActiveConstructionRecord = (m.Construction?.ConstructionRecords?.filter((r) => r.deleted_at === null).length ?? 0) > 0;
  const isFinishedConstruction = m.Construction?.finished_at != null;
  return hasActiveConstructionRecord || isFinishedConstruction;
}

export function materialStage(m: searchedMaterial): MaterialStage {
  // Consumptions が空(消費明細未対応レスポンス／ConstructionMaterialConsumption行の無い旧データ)のときは、
  // Σ による全量判定を行わず「紐づく施工の状態のみ」で判定する従来ロジックにフォールバックする。
  const hasConsumptionData = m.Consumptions.length > 0;
  const consumptions = toConsumptionLike(m);
  const fullyConsumed = !hasConsumptionData || isQrFullyConsumed(consumptions, m.id);
  const partiallyConsumed = hasConsumptionData && isQrPartiallyConsumed(consumptions, m.id);

  const completedConstruction = isConsumedMaterial(m);
  if (fullyConsumed && completedConstruction) {
    return { key: "consumed", label: "消費済（施工完了）", color: "default" };
  }
  const activeConstruction = !IsAnyDeleted(m.Construction, m.Construction?.ConstructionPlan, m.Construction?.ConstructionPlan?.Work);
  if (fullyConsumed && activeConstruction) {
    return { key: "planned", label: "施工計画済", color: "warning" };
  }
  const carriedIn = !IsAnyDeleted(m.CarryIn, m.CarryIn?.Work);
  if (partiallyConsumed && carriedIn) {
    return { key: "partial", label: "残量あり", color: "warning" };
  }
  if (carriedIn) {
    return { key: "in_stock", label: "在庫", color: "info" };
  }
  const shipped = !IsAnyDeleted(m.Lot, m.Lot?.CarryOut);
  if (shipped) {
    return { key: "shipped", label: "出荷済", color: "primary" };
  }
  return { key: "issued", label: "発行のみ", color: "default" };
}

// 材料QRが紐づくロットの material_id(=材料マスタID)。出荷前(ロット未紐付)は undefined。
export function materialIdOfQr(m: searchedMaterial): number | undefined {
  if (IsAnyDeleted(m.Lot, m.Lot?.CarryOut)) {
    return undefined;
  }
  return m.Lot?.material_id ?? undefined;
}

// 最終工程リンク: 施工 > 搬入 > 出荷(ロット) > 発行 の優先順で1つ。
export function lastStepLink(m: searchedMaterial): ReactNode {
  const construction = m.Construction;
  if (!IsAnyDeleted(construction) && !IsAnyDeleted(construction.ConstructionPlan) && !IsAnyDeleted(construction.ConstructionPlan?.Work)) {
    return <DataLink type="construction" id={construction.id} />;
  }
  const carryIn = m.CarryIn;
  if (!IsAnyDeleted(carryIn) && !IsAnyDeleted(carryIn.Work)) {
    return <DataLink type="carry-in" id={carryIn.id} />;
  }
  const carryOut = m.Lot?.CarryOut;
  if (!IsAnyDeleted(m.Lot) && !IsAnyDeleted(carryOut)) {
    return <DataLink type="carry-out" id={carryOut.id} />;
  }
  if (m.Issuance) {
    return <DataLink type="issuance" id={m.Issuance.id} />;
  }
  return "-";
}

// 各エンティティの登録ユーザを LifeStepRow の actor 形式へ。
export function actorOf(u?: { name: string; Group?: { name?: string | null } | null } | null): { name: string; group_name?: string } | null {
  if (!u) {
    return null;
  }
  return { name: u.name, group_name: u.Group?.name ?? undefined };
}

export const LINK_STYLE = { color: "#1976d2", textDecoration: "underline" } as const;

// 工事(Work)の表示名。short_name を優先し、空なら official_name を使う。
export function workLabel(work: { short_name: string; official_name: string }): string {
  return work.short_name || work.official_name;
}
